/**
 * Recovery for the one cache failure correct headers cannot prevent.
 *
 * Headers keep a NEW page load from being stale. They do nothing for a tab that
 * was already open when a deploy landed: that session holds the previous build's
 * chunk map, and each release replaces the hashed files it points at. The next
 * lazy route or Module-Federation remote it imports 404s, and the user gets a
 * crash screen for what is really just "your tab is out of date".
 *
 * The fix is to recognise that specific failure and reload once — the reload
 * fetches a no-store index.html, so it lands on the current build.
 *
 * Guarded by a timestamp in sessionStorage rather than a boolean: a boolean that
 * is never cleared stops a LATER deploy from recovering, and one that is cleared
 * on successful boot lets a genuinely broken deploy reload forever. A cooldown
 * does both jobs — at most one reload per window, and the next deploy is still
 * recoverable.
 *
 * The cooldown alone still permits an unbounded number of reloads over time, so
 * it is paired with a per-session CEILING. The two cover different failures: the
 * cooldown stops a crash-on-boot from spinning, the ceiling stops a permanently
 * missing asset (a half-uploaded release) from reloading every 30s all afternoon.
 * Past the ceiling the crash screen is the honest answer.
 */

import { isConnectivityLost } from '@clarix/utils';

const RELOAD_KEY = 'clarix.staleChunkReloadAt';
const RELOAD_COOLDOWN_MS = 30_000;
/** Recovery reloads allowed per tab session, across all deploys it lives through. */
const MAX_RELOADS = 3;

/**
 * Message signatures browsers and bundlers use when a dynamic import's file is
 * no longer on the server. They differ per engine, so match on all of them:
 * Chrome/Vite ("Failed to fetch dynamically imported module"), Firefox ("error
 * loading dynamically imported module"), Safari ("Importing a module script
 * failed"), Rollup/webpack chunk helpers, and Vite's CSS preloader.
 *
 * "Failed to load module script" is the same staleness wearing a different face:
 * a host whose SPA fallback answers 200 text/html for a deleted asset hands the
 * browser HTML where a module was expected, so the import fails on MIME rather
 * than on 404. Same cause, same cure.
 */
const STALE_CHUNK_PATTERN =
  /ChunkLoadError|Loading chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script|Unable to preload CSS/i;

export function isStaleChunkError(err: unknown): boolean {
  if (!err) return false;
  const text =
    err instanceof Error ? `${err.name}: ${err.message}` : typeof err === 'string' ? err : '';
  return STALE_CHUNK_PATTERN.test(text);
}

/** What this tab has already spent on recovery reloads. */
interface ReloadBudget {
  /** Epoch ms of the last recovery reload; 0 when none has happened. */
  at: number;
  /** Recovery reloads spent so far in this tab session. */
  count: number;
}

/** `null` when sessionStorage is unusable — the caller must then decline. */
function readBudget(): ReloadBudget | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(RELOAD_KEY);
  } catch {
    // Safari private mode and similar: storage throws on read. Without a guard a
    // reload could loop, so decline to reload at all and let the error surface.
    return null;
  }
  if (!raw) return { at: 0, count: 0 };

  try {
    const parsed: unknown = JSON.parse(raw);
    // A bare number is the pre-ceiling format — an in-flight session upgrading
    // mid-deploy still gets its cooldown honoured instead of a free reload.
    if (typeof parsed === 'number' && Number.isFinite(parsed)) return { at: parsed, count: 1 };
    if (parsed && typeof parsed === 'object') {
      const { at, count } = parsed as Partial<ReloadBudget>;
      return {
        at: typeof at === 'number' && Number.isFinite(at) ? at : 0,
        count: typeof count === 'number' && Number.isFinite(count) ? count : 0,
      };
    }
  } catch {
    /* fall through to "spent" */
  }
  // An unreadable value is treated as SPENT, never as empty: a parse fault must
  // not be able to authorise the very loop the budget exists to prevent.
  return { at: Date.now(), count: MAX_RELOADS };
}

/**
 * Reload once to pick up the current build. Returns true if a reload was
 * triggered, so callers can skip rendering an error state that is about to be
 * thrown away.
 */
export function recoverFromStaleChunk(reason: string): boolean {
  // A dynamic import that fails because the CONNECTION dropped reports the same
  // message as one whose file is gone — Chrome cannot tell the caller which it
  // was. Reloading an offline tab trades a running app for the browser's own
  // error page, and the user cannot even retry from there. Only an explicit
  // `false` blocks, so a runtime that doesn't report connectivity still recovers.
  // `isConnectivityLost` also covers wifi-without-internet once a probe saw it.
  if (isConnectivityLost()) return false;

  const budget = readBudget();
  if (!budget) return false;
  if (budget.count >= MAX_RELOADS) return false;
  if (Date.now() - budget.at < RELOAD_COOLDOWN_MS) return false;

  try {
    sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ at: Date.now(), count: budget.count + 1 }));
  } catch {
    return false;
  }

  console.warn(`[shell] stale build detected (${reason}) — reloading to the current version`);
  window.location.reload();
  return true;
}

/**
 * Catches the stale-chunk failures that never reach a React error boundary:
 * Vite's own preload helper, and dynamic imports rejected outside a component
 * (route prefetches, MF remote entries fetched by the runtime).
 */
export function installStaleChunkRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Stop Vite rethrowing — this failure is now handled by the reload.
    event.preventDefault();
    recoverFromStaleChunk('vite:preloadError');
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!isStaleChunkError(event.reason)) return;
    recoverFromStaleChunk('unhandled dynamic import rejection');
  });
}
